"use client";

import { type RefObject, useCallback, useEffect, useRef } from "react";

export type GuideCloseMethod = "esc" | "swipe" | "scrim";

/** 手指累计纵向位移超过它才算「下滑」，过滤点按时的抖动 */
export const SWIPE_DISTANCE_PX = 10;
/** 下滑后多久内触发的关闭仍归因于下滑 */
export const SWIPE_WINDOW_MS = 600;

/**
 * 引导弹窗关闭方式归因（`overlap_guide_close.method`）。
 *
 * 弹窗壳（EdgenModal）只告诉我们「被关了」，不说怎么关的，所以内容挂载期间自己旁听：
 * - `document` 上 capture 监听 `Escape` → `"esc"`；
 * - 根容器 `touchmove` 累计纵向位移 > 10px，600ms 内触发关闭 → `"swipe"`；
 * - 其余 → `"scrim"`。
 * 读取即重置，避免上一次的痕迹污染下一次。
 */
export const useGuideCloseMethod = (
  rootRef: RefObject<HTMLElement | null>,
): (() => GuideCloseMethod) => {
  const escRef = useRef(false);
  const swipeAtRef = useRef<number | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        escRef.current = true;
      }
    };
    document.addEventListener("keydown", onKeyDown, true);

    const root = rootRef.current;
    let lastY: number | null = null;
    let travelled = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0]?.clientY ?? null;
      travelled = 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (y === undefined || lastY === null) {
        return;
      }
      travelled += Math.abs(y - lastY);
      lastY = y;
      if (travelled > SWIPE_DISTANCE_PX) {
        swipeAtRef.current = Date.now();
      }
    };
    root?.addEventListener("touchstart", onTouchStart, { passive: true });
    root?.addEventListener("touchmove", onTouchMove, { passive: true });

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      root?.removeEventListener("touchstart", onTouchStart);
      root?.removeEventListener("touchmove", onTouchMove);
    };
  }, [rootRef]);

  return useCallback((): GuideCloseMethod => {
    const swipeAt = swipeAtRef.current;
    const method: GuideCloseMethod = escRef.current
      ? "esc"
      : swipeAt !== null && Date.now() - swipeAt < SWIPE_WINDOW_MS
        ? "swipe"
        : "scrim";
    escRef.current = false;
    swipeAtRef.current = null;
    return method;
  }, []);
};
